/**
 * Measures actual GPU frame time via the EXT_disjoint_timer_query_webgl2
 * extension, where available. GPU work is asynchronous, so a query's result
 * isn't ready the same frame it's issued — this keeps a small ring of
 * in-flight queries and reports a rolling average once results land.
 * Falls back to reporting 0 (shown as "n/a") on browsers/GPUs without the
 * extension (e.g. most mobile GPUs, some WebGL1-only contexts).
 */
export class GpuProfiler {
  private readonly gl: WebGL2RenderingContext | null;
  private readonly ext: any;
  private readonly pending: { query: WebGLQuery }[] = [];
  private readonly maxInFlight = 3;
  private lastMs = 0;
  private avgMs = 0;

  constructor(renderer: { getContext(): WebGLRenderingContext | WebGL2RenderingContext }) {
    const ctx = renderer.getContext();
    if (typeof WebGL2RenderingContext !== 'undefined' && ctx instanceof WebGL2RenderingContext) {
      this.gl = ctx;
      this.ext = ctx.getExtension('EXT_disjoint_timer_query_webgl2');
    } else {
      this.gl = null;
      this.ext = null;
    }
  }

  get supported(): boolean {
    return this.ext != null;
  }

  /** GPU frame time in milliseconds, as a rolling average. 0 if unsupported. */
  get milliseconds(): number {
    return this.avgMs;
  }

  begin(): void {
    if (!this.gl || !this.ext) return;
    if (this.pending.length >= this.maxInFlight) return; // avoid unbounded growth if results never resolve
    const query = this.gl.createQuery();
    if (!query) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, query);
    this.pending.push({ query });
  }

  end(): void {
    if (!this.gl || !this.ext || this.pending.length === 0) return;
    this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
  }

  /** Call once per frame after end() to harvest any results that are ready. */
  poll(): void {
    if (!this.gl || !this.ext) return;
    while (this.pending.length > 0) {
      const { query } = this.pending[0];
      const available = this.gl.getQueryParameter(query, this.gl.QUERY_RESULT_AVAILABLE);
      const disjoint = this.gl.getParameter(this.ext.GPU_DISJOINT_EXT);
      if (!available) break;
      this.pending.shift();
      if (disjoint) continue;
      const ns = this.gl.getQueryParameter(query, this.gl.QUERY_RESULT) as number;
      this.lastMs = ns / 1e6;
      // Simple exponential smoothing so the HUD number doesn't flicker.
      this.avgMs = this.avgMs === 0 ? this.lastMs : this.avgMs * 0.9 + this.lastMs * 0.1;
    }
  }
}
